import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/pages/RequestDetail.js (Download all button, buildReceiptFilename,
//     buildSummaryCSV, AttachmentList with ✕ delete control,
//     handleCleanupFiles, Lost receipts PDF button)
//   - src/lib/supabase.js (cleanupRequestFiles, deleteAttachmentAndRevert,
//     no archiveRequest function exists)
// Removed:
//   - "Green 'ready to review' badge on dashboard at the top" -- no such
//     badge; no special ordering for completed requests
//   - "Request re-upload" button on each file -- does not exist; workflow
//     is to delete the attachment with ✕, which reverts the row to pending
//   - Naming pattern "_Description_" -- actually "_Vendor_£Amount.ext"
//     where vendor comes from payee field or is extracted from description
//   - "_DECLARATION suffix" in zip -- lost receipts are NOT included as
//     files in the zip; they're shown in a separate PDF declaration
//     (Lost receipts PDF button) and listed in the summary.csv
//   - "Files stay accessible for seven years" -- no code-enforced retention
//   - "Archive a completed request from the menu" -- only clients can be
//     archived, not requests
// Pass 2 added:
//   - The actual workflow for replacing a bad receipt (delete the attachment,
//     row reverts to pending, ask client to upload again)
//   - summary.csv generated alongside the zipped files
//   - 📄 Lost receipts (PDF) button (appears when at least one lost row)
//   - 🗂 Permanently delete stored files button (appears after completion)

export default {
  title:   'Reviewing and downloading completed receipts',
  summary: 'How to quality-check what your client uploaded, download the audit-ready zip, and what to do with bad files.',
  body: (
    <>
      <P>
        A request is complete once every transaction has either a file
        or a lost-receipt declaration. You'll receive an email saying
        so if you have completion notifications turned on (default).
        The request page itself shows a green progress bar at 100%
        once everything is in.
      </P>

      <H>Quick review on the request page</H>
      <P>
        Open the request from the client's detail page. Each row shows
        the uploaded file as a small chip below the description (click
        to open). Rows with a lost-receipt declaration show the
        client's explanation in amber.
      </P>

      <Screenshot
        src="/support-screenshots/06 - completed-request.jpg"
        alt="A completed request page showing the header buttons (Send email, Lost receipts PDF, Permanently delete stored files, Copy client link, Cancel request, Delete request), a 100% Progress bar with 5 uploaded and 1 lost, and the transaction table below with file chips on each uploaded row and a lost-receipt explanation on the lost row."
        caption="A completed request showing the action buttons at the top, the full Progress bar, uploaded file chips on each row, and the lost-receipt explanation where applicable."
      />

      <P>
        The review pass is worth doing before downloading the zip.
        Common things to spot:
      </P>
      <UL>
        <Li>
          <strong>Blurry or partially-cropped photos.</strong>{' '}
          Phone-photographed receipts can be unreadable. Click the
          chip to open the file and check.
        </Li>
        <Li>
          <strong>Wrong receipt against a transaction.</strong>{' '}
          Clients sometimes upload the wrong receipt by mistake.
          Check the amount on the receipt matches the transaction
          amount.
        </Li>
        <Li>
          <strong>Lost receipt declarations that should have been
          findable.</strong> If a client marked a £600 PayPal payment
          as lost without trying, it may be worth a quick message.
        </Li>
      </UL>

      <H>Replacing a bad file</H>
      <P>
        Receipt Chaser doesn't have a "request re-upload" feature.
        Instead, remove the file yourself by clicking the ✕ next to it
        in the row. The file is deleted and the transaction reverts
        to pending. From there, the client's upload link works again
        for that row, so you can ask them to upload a fresh copy
        (a quick message or use the <Code>📧 Send email</Code> button
        at the top to re-send the request email).
      </P>

      <H>Downloading the zip</H>
      <P>
        Click <Code>⬇ Download all</Code> at the top of the request
        page. Receipt Chaser builds a zip containing:
      </P>
      <UL>
        <Li>Every uploaded file, renamed for easy identification</Li>
        <Li>
          A <Code>summary.csv</Code> covering every transaction in the
          request, including pending and lost ones, with notes and the
          renamed filenames so each receipt cross-references against
          a row
        </Li>
      </UL>

      <Screenshot
        src="/support-screenshots/06 - download-all.jpg"
        alt="The top of a completed request page with the Download all (5) button highlighted in an orange outline at the right side of the Progress card."
        caption="The Download all button at the top of a completed request, sitting next to the uploaded / lost counts."
      />

      <H>The renaming pattern</H>
      <P>
        Each file in the zip is renamed to make it identifiable
        without opening it:
      </P>
      <P>
        <Code>YYYY-MM-DD_Vendor_£Amount.ext</Code>
      </P>
      <P>
        So a £45.20 lunch on 14 October 2026 from "Pret a Manger"
        becomes <Code>2026-10-14_Pret_£45.20.pdf</Code>. The vendor
        is pulled from the payee column on exports that provide one
        (Xero and QuickBooks). For other formats it's extracted from
        the description. If the same vendor appears twice in one day
        at the same amount, the zip adds <Code>(2)</Code>,{' '}
        <Code>(3)</Code> and so on.
      </P>

      <Screenshot
        src="/support-screenshots/06 - renamed files.png"
        alt="A file browser view of an unzipped Receipt Chaser export folder showing five renamed receipt files (2026-02-16_DIRECT DEBIT PAYMENT_£5331.92.png, 2026-02-17_AMAZON_£13.99.pdf, and so on) plus a summary.csv at the bottom."
        caption="A real download: each receipt renamed by date, vendor, and amount, plus a summary.csv that cross-references every transaction back to its file."
        width={520}
      />

      <H>Lost receipt declarations</H>
      <P>
        Lost-receipt rows are NOT included as separate files in the
        zip. The <Code>summary.csv</Code> records them with the
        client's explanation. For a separate PDF declaration suitable
        for client records or HMRC enquiries, click{' '}
        <Code>📄 Lost receipts (PDF)</Code> at the top of the request
        page (only visible when at least one row is marked as lost).
        See <Link to="/support/handling-lost-receipts">Handling lost receipts</Link>.
      </P>

      <Screenshot
        src="/support-screenshots/06 - lost-receipt-declaration.jpg"
        alt="The top of a completed request page with the Lost receipts (PDF) button highlighted in an orange outline, sitting between Send email and Permanently delete stored files in the header buttons."
        caption="The Lost receipts (PDF) button at the top of the request page. Only appears when at least one row is marked as lost."
      />

      <Callout variant="tip" title="Where the zip goes next">
        Most bookkeepers attach the contents of the zip to the
        relevant transactions in their accounting software (Xero's
        "files attached" feature, Sage's document storage, etc.) and
        keep a copy of the zip itself in their long-term records.
        Receipt Chaser also keeps the originals on file for as long
        as the request exists.
      </Callout>

      <H>Cleaning up stored files after download</H>
      <P>
        Once you've saved the zip to your own system, you can free up
        Receipt Chaser's storage by clicking{' '}
        <Code>🗂 Permanently delete stored files</Code> at the top of
        the request. This button only appears on requests where every
        transaction is resolved and at least one file is uploaded.
      </P>
      <P>
        Deleting stored files removes the original receipt files but
        keeps the request itself (transactions, notes, declarations).
        The download button disappears since there's nothing left to
        re-download.
      </P>
      <Callout variant="info" title="Only after you have the zip">
        This action is permanent and can't be undone. Only use it
        once you have the zip safely in your own system. Most
        bookkeepers wait until they've added the receipts to their
        accounting software before cleaning up.
      </Callout>

      <H>Coming back to a completed request</H>
      <P>
        Completed requests stay on the client's detail page under the{' '}
        <Code>Completed</Code> tab. Open one any time to re-download
        the zip (assuming you haven't cleaned up stored files), check
        a specific transaction, or add internal notes.
      </P>

      <H>Related guides</H>
      <UL>
        <Li>
          <Link to="/support/handling-lost-receipts">Handling lost receipts</Link>
        </Li>
        <Li>
          <Link to="/support/resending-or-editing-a-request">Resending or editing a request</Link>
        </Li>
      </UL>
    </>
  ),
}
